import { useState } from "react";
import NavBar from "../components/navBar/navBar";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import useGetLogsEmails from "../hooks/useGetLogsEmails";
import Loader from "../components/Loader";
import { showApiErrorToast } from "../utils/helpers";

const columns = [
  { id: "email", label: "Email", minWidth: 170 },
  { id: "role", label: "Role", minWidth: 170 },
  { id: "full_name", label: "Full Name", minWidth: 170 },
];

const UserActionLogs = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();

  const { data: userLogs = [], isLoading, isError, error } = useGetLogsEmails();

  if (isError) {
    showApiErrorToast(error, "Failed to fetch user logs");
  }

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRowClick = (email) => {
    navigate("/patient/single-user-logs", { state: { email } });
  };

  const filteredLogs = userLogs.filter(
    (log) =>
      log.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="user-action-logs" />
      <Box sx={{ height: "calc(100vh - 64px)" }}>
        <Paper
          sx={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              pt: 2,
              pb: 1,
            }}
          >
            <Box
              sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "flex-end" }}
              width={"100%"}
            >
              <TextField
                size="small"
                placeholder="Search by Email, Name, or Role"
                value={searchTerm}
                onChange={handleSearch}
                sx={{ width: 260 }}
              />
              <FormControl size="small" sx={{ width: 140 }}>
                <Select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                  {[10, 25, 50, 100].map((item) => (
                    <MenuItem key={item} value={item}>
                      Show {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer sx={{ flexGrow: 1 }}>
            <Table stickyHeader aria-label="user action logs table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align="left"
                      sx={{
                        minWidth: column.minWidth,
                        fontWeight: "bold",
                        fontSize: "1.01rem",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Loader />
                    </TableCell>
                  </TableRow>
                ) : filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No users found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={index}
                        onClick={() => handleRowClick(row.email)}
                        sx={{ cursor: "pointer" }}
                      >
                        {columns.map((column) => (
                          <TableCell key={column.id} align="left">
                            {row[column.id]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredLogs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default UserActionLogs;
